import { buttonStyle } from 'components/buttons/buttonStyle';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { SECTION_CARD, SECTION_HEADER, SECTION_TITLE } from '../details-tab/SectionCard';

/**
 * The columns a totals table's header and rows share: description (3 of 12), discount (1), total without VAT
 * (2), VAT (1), total with VAT (2), total in payment currency (2) and the row's button (1).
 */
export const TOTALS_GRID = 'grid grid-cols-12 items-center gap-x-2 sm:gap-x-2.5 lg:gap-x-4';

/** One row of a totals table. The last one has no rule under it. */
export const TOTALS_ROW = 'border-divider border-b p-4 last:border-b-0';

/** A figure in a totals row: 12px on the body's line height. */
export const TOTALS_TEXT = 'text-[12px] leading-[1.375rem]';

/** The grey caption under a row's description. */
export const TOTALS_CAPTION = 'text-fg-secondary text-[10px] new:text-caption leading-[1.375rem]';

/** The compact outlined button of a totals table, in its title row or on a row; a text button in the new design. */
export const totalsButtonStyle = (width: string) =>
    buttonStyle(
        { variant: 'outlined', size: 'extraSmall' },
        cn(
            'h-[25px] px-0 text-[10px] font-semibold',
            width,
            'new:text-primary new:hover:bg-muted new:hover:text-primary new:text-meta new:h-8 new:w-auto new:gap-1.5 new:border-0 new:bg-transparent new:px-2.5 new:font-medium new:shadow-none'
        )
    );

/**
 * The always-open box of one kind of totals in a case (fees, other fees, admin fees, expenses): its title row,
 * then the table. A `sticky` title row stays pinned while the pane scrolls: the negative `top` is the pane's
 * padding at each container width, plus the radius. In the new design it is a block of the case's Case totals card:
 * a heading over a rule, "None" when it has no rows, and a table that scrolls sideways in a narrow card.
 */
export const TotalsTableCard = (props: {
    title: ReactNode;
    actions?: ReactNode;
    sticky?: boolean;
    /** What sits between the title row and the table's rule; in the new design, beside the title in its row. */
    subheader?: ReactNode;
    /** No rows: the new design says so under the heading instead of drawing the table's header. */
    isEmpty?: boolean;
    children: ReactNode;
}) => (useIsNewDesign() ? <CaseTotalsBlock {...props} /> : <ClassicTotalsTableCard {...props} />);

const CaseTotalsBlock = ({
    title,
    actions,
    subheader,
    isEmpty,
    children,
}: {
    title: ReactNode;
    actions?: ReactNode;
    subheader?: ReactNode;
    isEmpty?: boolean;
    children: ReactNode;
}) => (
    <div className="border-divider border-t">
        <div className="flex min-h-11 flex-wrap items-center gap-x-4 gap-y-2 px-5 py-1.5">
            <h3 className="text-meta text-foreground-secondary m-0 font-semibold">{title}</h3>
            {subheader}
            {actions ? <div className="ms-auto">{actions}</div> : null}
        </div>
        {isEmpty ? (
            <p className="text-meta text-muted-foreground m-0 px-5 pb-3">
                <FormattedMessage id="invoices.totals-tab.none" />
            </p>
        ) : (
            <div className="overflow-x-auto">
                <div className="min-w-[36rem]">{children}</div>
            </div>
        )}
    </div>
);

const ClassicTotalsTableCard = ({
    title,
    actions,
    sticky = false,
    subheader,
    children,
}: {
    title: ReactNode;
    actions?: ReactNode;
    sticky?: boolean;
    subheader?: ReactNode;
    children: ReactNode;
}) => (
    <div className={cn(SECTION_CARD, 'mb-[15px]')}>
        <div
            className={cn(
                SECTION_HEADER,
                'px-2',
                sticky && 'bg-paper sticky top-[-30px] z-1 rounded-md @max-5xl:top-[-22px] @max-3xl:top-[-16px]'
            )}
        >
            <div className="my-2.5 ml-2 flex grow items-center justify-between gap-2">
                <span className={SECTION_TITLE}>{title}</span>
                {actions}
            </div>
        </div>
        {subheader}
        <div className="border-divider border-t">
            <div className="overflow-hidden rounded-[7px]">{children}</div>
        </div>
    </div>
);
